import styled from 'styled-components';
import { P2 } from 'pfm-ui-components';
import Link from 'next/link';
import { Button } from 'pfm-ui-components';
import { routes } from '../../../config/routes';
import { up, between } from 'styled-breakpoints';
import * as constants from '../../../constants/NavigationTab';
import { getIconForNavigationTab } from '../NavigationFooterTab/util';
import { pxToRem } from '../../../styles/utils';
import Container from '../Container';
import { navigateToInvest } from '../../../API/router';
import { featureToggle } from '../../../constants/common';
import { useRouter } from 'next/router';
import { useAppContext } from '../../../store';


interface NavigationTabProps {
  hasPortfolio: boolean; // used to show portfolio tab
  activeMenu: number; // to highlight the current tab
  hasNotificationIcon: boolean; // used to show notification icon
  onOpenNotification: Function;
  notificationCount?: number;
  hideNotificationCount?: boolean;
  isLoggedIn?: boolean;
  onLoginToViewPortfolioButtonClick?: Function;
  enableFAQ: boolean;
  enableNotification: boolean;
  onUserGuideClick?: Function;
}

export default function NavigationTab(props: NavigationTabProps) {
  const {
    hasPortfolio,
    activeMenu,
    hasNotificationIcon,
    onOpenNotification,
    notificationCount,
    hideNotificationCount,
    isLoggedIn,
    onLoginToViewPortfolioButtonClick,
    enableFAQ,
    enableNotification,
    onUserGuideClick
  } = props;

  const router = useRouter();
  const { state } = useAppContext();

  return (
    <NavigationContainer>
      <Wrapper>
        {isLoggedIn && (
          <NavigationWrapper>
            <MenuContainer>
              <Link href={`${routes.INVEST.url}`}>
                <MenuWrapper enableBottomBorder={activeMenu === 1}>
                  {activeMenu === 1
                    ? getIconForNavigationTab('InvestmentBurgundySvg')
                    : getIconForNavigationTab('InvestmentGreySvg')}
                  <StyledP2 active={activeMenu === 1}>{constants.INVESTMENT}</StyledP2>
                </MenuWrapper>
              </Link>
              <Link href={`${routes.MUTUAL_FUND.url}`}>
                <MenuWrapper enableBottomBorder={activeMenu === 2}>
                  {activeMenu === 2
                    ? getIconForNavigationTab('ExploreBurgundySvg')
                    : getIconForNavigationTab('ExploreGreySvg')}
                  <StyledP2 active={activeMenu === 2}>{constants.INVEST}</StyledP2>
                </MenuWrapper>
              </Link>
              <Link href={`${routes.PORTFOLIO.url}`}>
                <PortfolioWrapper showPortfolio={hasPortfolio} enableBottomBorder={activeMenu === 3}>
                  <UserGuideSpotlight id='mf_user_guide_portfolio_nav_header'>
                    {activeMenu === 3
                      ? getIconForNavigationTab('FolioBurgundySvg')
                      : getIconForNavigationTab('FolioGreySvg')}
                    <StyledP2 active={activeMenu === 3}>{constants.PORTFOLIO}</StyledP2>
                  </UserGuideSpotlight>
                </PortfolioWrapper>
              </Link>
              {enableFAQ && (
                <Link href={`${routes.FAQ.url}`}>
                  <MenuWrapper enableBottomBorder={activeMenu === 4}>
                    {activeMenu === 4 ? getIconForNavigationTab('FAQBurgundySvg') : getIconForNavigationTab('FAQSvg')}
                    <StyledP2 active={activeMenu === 4}>{constants.FAQS}</StyledP2>
                  </MenuWrapper>
                </Link>
              )}
              <Link href={`${routes.PROFILE.url}`}>
                  <MenuWrapper enableBottomBorder={activeMenu === 5}>
                    <UserGuideSpotlight id='folio_cards_profile_header'>
                      {activeMenu === 5 ? getIconForNavigationTab('MyProfileBurgundySvg') : getIconForNavigationTab('MyProfileSvg')}
                      <StyledP2 active={activeMenu === 5}>{constants.MY_PROFILE}</StyledP2>
                    </UserGuideSpotlight>
                  </MenuWrapper>
                </Link>
              {(router.pathname === "/mutualFunds" || router.pathname === "/portfolio") && (
                  <MenuWrapper onClick={() => onUserGuideClick()} enableBottomBorder={false}>
                    {getIconForNavigationTab('UserGuideSvg')}
                    <StyledP2>{constants.USER_GUIDE}</StyledP2>
                  </MenuWrapper>
              )}
            </MenuContainer>
            <MenuContainer>
            {enableNotification && (
              <NotificationIconWrapper onClick={() => onOpenNotification()}>
                <NotificationContainer enableNotification={hasNotificationIcon}>
                  {getIconForNavigationTab('NotificationSvg')}
                  {notificationCount ? (
                    <NotificationCounter disableNotificationCount={hideNotificationCount}>
                      {notificationCount}
                    </NotificationCounter>
                  ) : (
                    ''
                  )}
                </NotificationContainer>
              </NotificationIconWrapper>
            )}
            <Link href={`${routes.CART.url}`}>
              <CartIconWrapper>
                <CartContainer enableCart={true}>
                  {getIconForNavigationTab('CartSvg')}
                  {state.cartSchemeCount ? <CartCounter>{state.cartSchemeCount}</CartCounter> : ''}
                </CartContainer>
              </CartIconWrapper>
            </Link>
            </MenuContainer>
          </NavigationWrapper>
        )}

        {isLoggedIn !== undefined && !isLoggedIn && (
          <ButtonWrapper>
            <StyledButton primary onClick={onLoginToViewPortfolioButtonClick}>
              {constants.LOGIN_TO_VIEW_PORTFOLIO}
            </StyledButton>
          </ButtonWrapper>
        )}
      </Wrapper>
    </NavigationContainer>
  );
}

const NavigationContainer = styled(Container)``;

const Wrapper = styled.div`
  display: flex;
  height: ${pxToRem(60)};
`;

const NavigationWrapper = styled.article`
  max-width: 100%;
  width: 100%;
  height: ${pxToRem(60)};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  position: relative;
`;

const MenuContainer = styled.article`
  display: flex;
`;

const MenuWrapper = styled.article<{ enableBottomBorder: boolean }>`
  ${between('md', 'lg')} {
    padding: 0 ${pxToRem(8)} ${pxToRem(12)} ${pxToRem(8)};
  }
  display: flex;
  align-items: center;
  justify-content: center;
  border: ${pxToRem(4)} solid transparent;
  border-bottom-style: ${(props) => (props.enableBottomBorder ? 'insert' : 'initial')};
  border-bottom-color: ${(props) => props.theme.palette.burgundy1};
  padding: 0 ${pxToRem(16)} ${pxToRem(12)} ${pxToRem(16)};
  margin-top: 12px;
`;

const PortfolioWrapper = styled(MenuWrapper)<{ showPortfolio: boolean }>`
  display: ${(props) => (props.showPortfolio ? 'flex' : 'none')};
`;

const NotificationIconWrapper = styled.article`
  cursor: pointer;
  align-self: center;
  ${up('md')} {
    padding-left: ${pxToRem(60)};
  }
  position: relative;
  ${up('lg')} {
    padding-left: 0;
  }
`;

const NotificationContainer = styled.article<{ enableNotification: boolean }>`
  position: relative;
  display: ${(props) => (props.enableNotification ? 'flex' : 'none')};
  padding: ${pxToRem(8)} ${pxToRem(22)} 0 0;
`;
const NotificationCounter = styled(P2)`
  position: absolute;
  left: ${pxToRem(8)};
  bottom: ${pxToRem(12)};
  border-radius: ${pxToRem(50)};
  width: ${pxToRem(16)};
  height: ${pxToRem(14)};
  color: ${(props) => props.theme.palette.white};
  line-height: ${pxToRem(12)};
  font-size: ${pxToRem(10)};
  text-align: center;
  background-color: #97144d;
  display: ${(props) => (props.disableNotificationCount ? 'none' : 'inline')};
`;

const CartIconWrapper = styled.article`
  cursor: pointer;
  align-self: center;
  padding-left: ${pxToRem(8)};
  position: relative;
`;

const CartContainer = styled.article<{ enableCart?: boolean }>`
  position: relative;
  display: ${(props) => (props.enableCart ? 'flex' : 'none')};
  padding: 0 ${pxToRem(8)} 0 0;
`;
const CartCounter = styled(P2)`
  position: absolute;
  left: ${pxToRem(24)};
  bottom: ${pxToRem(24)};
  border-radius: ${pxToRem(50)};
  width: ${pxToRem(16)};
  height: ${pxToRem(14)};
  color: ${(props) => props.theme.palette.white};
  line-height: ${pxToRem(12)};
  font-size: ${pxToRem(10)};
  text-align: center;
  background-color: #4a4a4a;
  display: ${(props) => (props.disableNotificationCount ? 'none' : 'inline')};
`;

const StyledP2 = styled(P2)`
  line-height: ${pxToRem(17)};
  cursor: pointer;
  color: ${(props) => (props.active ? props.theme.palette.burgundy1 : '#4a4a4a')};
  padding-left: ${pxToRem(8)};
`;

const ButtonWrapper = styled.div`
  display: flex;
  position: relative;
  width: 100%;
  margin-right: ${pxToRem(16)};
`;

const StyledButton = styled(Button)`
  align-self: center;
  position: absolute;
  right: 0;
`;

const UserGuideSpotlight = styled.div`
  display: flex;
  align-items: center
`;